import { parseFilesystemMemberReference } from "./contextSelection.ts";
import { collectionIdOfMember } from "./collectionRows.ts";
import {
  parseRoomMemberId,
  worldVfsMemberKind,
  CANVAS_TYPE_LABELS,
  isCanvasContentType,
  type CanvasContentType,
} from "./roomUniverse.ts";

/** Purpose labels come from saved Canvas metadata; routing keeps stable member ids. */
export function roomMemberTypeLabel(memberId: string, contentType?: CanvasContentType): string {
  if (worldVfsMemberKind(memberId) === "canvas" && isCanvasContentType(contentType))
    return CANVAS_TYPE_LABELS[contentType];
  // PR J: display only. A collection keeps its world-convex member type (and
  // everything that type decides); its label just says what it is.
  if (collectionIdOfMember(memberId)) return "Collection";
  const kind = roomMemberType(memberId);
  if (kind === "company") return "Company";
  if (kind === "contact") return "Contact";
  return kind === "blueprint" ? "Idea" : kind === "canvas" ? "Canvas" : kind;
}

/**
 * The room member type contract (CB20; the principal's D-CT5 ruling
 * 2026-08-27: "something like a type system, like in a file system, the
 * file type, like DOC or XLS"). One computed fold from member IDENTITY
 * alone — group + contractual path grammar + file extension — to one
 * display type. Never a stored field, never a rail-local taxonomy: context
 * menus, open-face routing, and type icons all derive from this one fold.
 *
 * The fold is total over the member-id space: an unparseable id types as
 * the `file` fallback rather than throwing (the rail renders what the
 * catalog carries), and the Words corpus types as `corpus` even though the
 * CB16 display law keeps it out of the rail.
 */
export type RoomMemberType =
  | "document"
  | "spreadsheet"
  | "image"
  | "video"
  | "audio"
  | "code"
  | "file"
  | "canvas"
  | "blueprint"
  | "component"
  | "corpus"
  | "company"
  | "contact";

const EXTENSION_TYPES: Readonly<Record<string, RoomMemberType>> = {
  // documents
  md: "document",
  mdx: "document",
  txt: "document",
  rtf: "document",
  pdf: "document",
  doc: "document",
  docx: "document",
  pages: "document",
  // spreadsheets
  xls: "spreadsheet",
  xlsx: "spreadsheet",
  csv: "spreadsheet",
  tsv: "spreadsheet",
  numbers: "spreadsheet",
  // images
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  svg: "image",
  heic: "image",
  avif: "image",
  // video
  mp4: "video",
  mov: "video",
  webm: "video",
  mkv: "video",
  m4v: "video",
  avi: "video",
  // audio
  mp3: "audio",
  wav: "audio",
  m4a: "audio",
  flac: "audio",
  ogg: "audio",
  aac: "audio",
  // code and structured text
  ts: "code",
  tsx: "code",
  js: "code",
  jsx: "code",
  mjs: "code",
  cjs: "code",
  py: "code",
  rb: "code",
  go: "code",
  rs: "code",
  swift: "code",
  java: "code",
  kt: "code",
  c: "code",
  h: "code",
  cpp: "code",
  hpp: "code",
  m: "code",
  mm: "code",
  cs: "code",
  sh: "code",
  zsh: "code",
  bash: "code",
  sql: "code",
  html: "code",
  css: "code",
  scss: "code",
  json: "code",
  jsonl: "code",
  yaml: "code",
  yml: "code",
  toml: "code",
  xml: "code",
};

const extensionType = (key: string): RoomMemberType => {
  const name = key.split("/").pop() ?? "";
  const dot = name.lastIndexOf(".");
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
  return EXTENSION_TYPES[extension] ?? "file";
};

export const roomMemberType = (memberId: string): RoomMemberType => {
  const physical = parseFilesystemMemberReference(memberId);
  if (physical) return physical.name ? extensionType(physical.name) : "file";
  if (/^world-vfs:crm\/companies\/[a-zA-Z0-9_-]+$/.test(memberId)) return "company";
  if (/^world-vfs:crm\/contacts\/[a-zA-Z0-9_-]+$/.test(memberId)) return "contact";
  const library = /^world-vfs:library\/(image|video|audio|component|blueprint)\//.exec(
    memberId,
  )?.[1];
  if (library) return library as RoomMemberType;
  const parsed = parseRoomMemberId(memberId);
  if ("_tag" in parsed) return "file";
  switch (parsed.group) {
    case "world-convex":
      return "corpus";
    case "world-vfs": {
      const kind = worldVfsMemberKind(memberId);
      return kind === "canvas"
        ? "canvas"
        : kind === "blueprint"
          ? "blueprint"
          : kind === "board-component"
            ? "component"
            : "file";
    }
    default:
      return extensionType(parsed.key);
  }
};
